import React, { useState, useEffect, useCallback, useRef, useMemo, useId } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { List, CaretLeft, DotsSixVertical, Check, CirclesThree } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { queryKeys } from '../queries/keys';
import { fetchItemDetail } from '../queries/items';
import { invalidateCollectionsForItems } from '../queries/collections';
import Sidebar from '../components/Sidebar';
import { CardGridSkeleton } from '../components/Skeleton';
import Toast from '../components/Toast';
import EmptyState from '../components/EmptyState';
import ItemCard from '../components/ItemCard';
import ItemDetailModal from '../components/ItemDetailModal';
import Modal, { CONFIRM_PANEL_MOTION } from '../components/Modal';
import { cardAccessibleName } from '../utils/accessibleName';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

function SortableItem({ item, isSelectMode, isSelected, onToggleSelect, onClick }) {
  const [thumbError, setThumbError] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${item.item_type}-${item.id}`, disabled: isSelectMode });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
  };

  const handleClick = () => {
    if (isSelectMode) {
      onToggleSelect(item);
    } else {
      onClick(item);
    }
  };

  // For enrichment items, use the old custom card (ItemCard is designed for items)
  if (item.item_type === 'enrichment') {
    // Accessible name states what the card IS, not "card" or "button".
    const accessibleName = cardAccessibleName(item.entity_name, item.entity_type, { fallbackType: 'entity' });

    return (
      // onClick lives here, not on the inner <button>, so the select-mode
      // checkbox below — a sibling, positioned on top of the button's corner
      // — still toggles selection when clicked directly (COL-91 review). The
      // button's own click (mouse or keyboard-synthesized) still reaches this
      // handler via bubbling.
      <div ref={setNodeRef} style={style} onClick={handleClick}>
        {!isSelectMode && (
          <div className="collection-item-drag-handle" {...attributes} {...listeners}>
            <DragIcon />
          </div>
        )}
        {isSelectMode && (
          <div className={`collection-item-checkbox ${isSelected ? 'checked' : ''}`}>
            {isSelected && <CheckIcon />}
          </div>
        )}
        <button
          type="button"
          className="collection-item-card card-button-reset"
          aria-label={accessibleName}
        >
          <div className="collection-item-thumb">
            {item.thumbnail_url && !thumbError ? (
              <img
                src={item.thumbnail_url}
                alt=""
                loading="lazy"
                onError={() => setThumbError(true)}
              />
            ) : (
              <div className="collection-item-no-thumb">
                <EntityIcon />
              </div>
            )}
          </div>
          <div className="collection-item-info">
            <span className="collection-item-title">
              {item.entity_name || 'Untitled'}
            </span>
            <span className="collection-item-type">
              {item.entity_type || 'entity'}
            </span>
          </div>
        </button>
      </div>
    );
  }

  // For regular items, use ItemCard with presigned thumbnail_url
  const mappedItem = { ...item, image: item.thumbnail_url };

  return (
    <div ref={setNodeRef} style={style}>
      {!isSelectMode && (
        <div className="collection-item-drag-handle" {...attributes} {...listeners}>
          <DragIcon />
        </div>
      )}
      {isSelectMode && (
        <div className={`collection-item-checkbox ${isSelected ? 'checked' : ''}`}>
          {isSelected && <CheckIcon />}
        </div>
      )}
      <ItemCard item={mappedItem} onClick={() => handleClick()} />
    </div>
  );
}

export default function CollectionDetail() {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  useScrollRestoration();
  const confirmDeleteTitleId = useId();
  const queryClient = useQueryClient();
  const collectionKey = queryKeys.collection(user?.id, id);
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const toastTimeoutRef = useRef(null);

  // Item detail modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const [loadingItemId, setLoadingItemId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const showToast = useCallback((message, type = 'info') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Cache-first: revisiting a collection (or navigating back to it) renders
  // instantly from cache while react-query revalidates in the background.
  // enabled: !!user?.id is a second, query-level guard against ever firing
  // under the shared 'anon' key namespace (see queries/keys.js) — belt and
  // braces alongside ProtectedRoute never mounting this page pre-auth.
  const { data: collection, isLoading: loading, isError } = useQuery({
    queryKey: collectionKey,
    queryFn: () => api.getCollection(id),
    enabled: !!user?.id,
  });
  const items = collection?.items ?? [];

  useEffect(() => {
    if (isError) showToast('Failed to load collection', 'error');
  }, [isError, showToast]);

  useEffect(() => {
    if (collection) {
      setEditName(collection.name);
      setEditDescription(collection.description || '');
    }
  }, [collection]);

  // All collections for sidebar nav — shares its cache entry with the
  // MyCollections list page (same query key), so navigating between them
  // and back here is instant.
  const { data: myCollectionsData } = useQuery({
    queryKey: queryKeys.myCollections(user?.id),
    queryFn: () => api.getMyCollections(),
    enabled: !!user?.id,
  });
  const allCollections = myCollectionsData?.collections ?? [];

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  // Escape-to-close is handled by the shared Modal primitive (COL-89) —
  // no per-page keydown listener needed here anymore.
  const updateMutation = useMutation({
    mutationFn: (payload) => api.updateCollection(id, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(collectionKey, (prev) => prev ? { ...prev, ...updated } : prev);
      setIsEditing(false);
      showToast('Collection updated', 'success');
    },
    onError: () => showToast('Failed to update collection', 'error'),
  });

  const deleteCollectionMutation = useMutation({
    mutationFn: () => api.deleteCollection(id),
    onSuccess: () => {
      showToast('Collection deleted', 'success');
      navigate('/collections');
    },
    onError: () => showToast('Failed to delete collection', 'error'),
  });

  // No hook-level onError — the two call sites (bulk select-mode removal vs.
  // single item delete from the modal) show differently-worded toasts.
  const removeItemsMutation = useMutation({
    mutationFn: (itemsToRemove) => api.removeItemsFromCollection(id, itemsToRemove),
    onSuccess: (_, itemsToRemove) => {
      const removedKeys = new Set(itemsToRemove.map(i => `${i.item_type}-${i.item_id}`));
      queryClient.setQueryData(collectionKey, (prev) => prev ? {
        ...prev,
        items: (prev.items ?? []).filter(item => !removedKeys.has(`${item.item_type}-${item.id}`)),
        item_count: Math.max(0, (prev.item_count ?? 0) - itemsToRemove.length),
      } : prev);
      // CollectionPickerModal caches each item's "which collections is it
      // in" membership (COL-96) — invalidate it for every removed item so
      // reopening the picker doesn't still show this collection as
      // already-added.
      invalidateCollectionsForItems(queryClient, user?.id, itemsToRemove);
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (reorderPayload) => api.reorderCollectionItems(id, reorderPayload),
    onError: () => {
      showToast('Failed to save order', 'error');
      queryClient.invalidateQueries({ queryKey: collectionKey }); // Revert on failure
    },
  });

  const handleSaveEdit = () => {
    if (!editName.trim()) return;
    updateMutation.mutate({
      name: editName.trim(),
      description: editDescription.trim() || null,
    });
  };

  const handleDeleteCollection = () => {
    if (deleteCollectionMutation.isPending) return;
    deleteCollectionMutation.mutate();
  };
  const isDeleting = deleteCollectionMutation.isPending;

  const handleToggleSelect = (item) => {
    const key = `${item.item_type}-${item.id}`;
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleRemoveSelected = async () => {
    if (removeItemsMutation.isPending) return;
    const itemsToRemove = items
      .filter(item => selectedItems.has(`${item.item_type}-${item.id}`))
      .map(item => ({ item_id: item.id, item_type: item.item_type }));

    try {
      await removeItemsMutation.mutateAsync(itemsToRemove);
      showToast(`Removed ${itemsToRemove.length} item${itemsToRemove.length === 1 ? '' : 's'}`, 'success');
      setSelectedItems(new Set());
      setIsSelectMode(false);
    } catch {
      showToast('Failed to remove items', 'error');
    }
  };
  const isRemoving = removeItemsMutation.isPending;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex(item => `${item.item_type}-${item.id}` === active.id);
    const newIndex = items.findIndex(item => `${item.item_type}-${item.id}` === over.id);

    const newItems = arrayMove(items, oldIndex, newIndex);
    // Optimistic reorder in the cache
    queryClient.setQueryData(collectionKey, (prev) => prev ? { ...prev, items: newItems } : prev);

    const reorderPayload = newItems.map((item, idx) => ({
      item_id: item.id,
      item_type: item.item_type,
      sort_order: idx,
    }));
    reorderMutation.mutate(reorderPayload);
  };

  const handleItemClick = (item, index) => {
    if (item.item_type === 'enrichment') {
      navigate(`/entities?enrichment=${item.id}`);
      return;
    }

    // Open detail modal for regular items
    const mappedItem = { ...item, image: item.thumbnail_url };
    setSelectedItem(mappedItem);
    setSelectedItemIndex(index);

    // Fetch full item details in background, sharing the cache with the
    // item detail queries used elsewhere (Gallery, Places).
    setLoadingItemId(item.id);
    fetchItemDetail(queryClient, user?.id, item.id).then(fullItem => {
      if (fullItem) {
        setSelectedItem(prev => prev && (prev.id === fullItem.id) ? { ...fullItem, image: fullItem.image || item.thumbnail_url } : prev);
      }
    }).catch(() => {}).finally(() => setLoadingItemId(null));
  };

  const handleModalClose = () => {
    setSelectedItem(null);
    setSelectedItemIndex(-1);
  };

  // Memoize item-type entries for modal navigation
  const itemTypeItems = useMemo(() => items.filter(i => i.item_type === 'item'), [items]);

  // Use selectedItemIndex for robust prev/next (not fragile find/indexOf)
  const handleModalPrev = () => {
    for (let i = selectedItemIndex - 1; i >= 0; i--) {
      if (items[i].item_type === 'item') {
        handleItemClick(items[i], i);
        return;
      }
    }
  };

  const handleModalNext = () => {
    for (let i = selectedItemIndex + 1; i < items.length; i++) {
      if (items[i].item_type === 'item') {
        handleItemClick(items[i], i);
        return;
      }
    }
  };

  const hasPrevItem = selectedItemIndex >= 0 &&
    items.slice(0, selectedItemIndex).some(i => i.item_type === 'item');

  const hasNextItem = selectedItemIndex >= 0 &&
    items.slice(selectedItemIndex + 1).some(i => i.item_type === 'item');

  // Delete removes item from collection (not permanent delete)
  const [confirmItemDelete, setConfirmItemDelete] = useState(false);
  const handleDeleteItem = async () => {
    if (!selectedItem) return;
    const itemId = selectedItem.id || selectedItem.item_id;
    try {
      await removeItemsMutation.mutateAsync([{ item_id: itemId, item_type: 'item' }]);
      handleModalClose();
      showToast('Removed from collection', 'success');
    } catch {
      showToast('Failed to remove item', 'error');
    }
  };

  const sidebarProps = {
    user,
    onLogout: logout,
    isOpen: sidebarOpen,
    onClose: () => setSidebarOpen(false),
    collectionsConfig: { collections: allCollections, activeCollectionId: id },
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar {...sidebarProps} />
        <main className="main-content">
          <div className="content-body">
            <CardGridSkeleton minWidth="200px" count={8} />
          </div>
        </main>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="app-layout">
        <Sidebar {...sidebarProps} />
        <main className="main-content">
          <EmptyState
            title="Collection not found"
            description="This collection may have been deleted."
            action={<button className="btn btn-primary" onClick={() => navigate('/collections')}>Back to Collections</button>}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar {...sidebarProps} />

      <main className="main-content">
        <header className="content-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
            <button
              className="btn btn-ghost btn-icon sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </button>
            <button
              className="btn btn-ghost btn-icon"
              onClick={() => navigate('/collections')}
              title="Back to collections"
            >
              <BackIcon />
            </button>
            {isEditing ? (
              <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  maxLength={100}
                  className="collection-input collection-input-inline"
                  autoFocus
                />
                <button className="btn btn-primary btn-sm" onClick={handleSaveEdit}>Save</button>
                <button className="btn btn-ghost btn-sm" onClick={() => { setIsEditing(false); setEditName(collection.name); }}>Cancel</button>
              </div>
            ) : (
              <h1 className="page-title" style={{ cursor: 'pointer' }} onClick={() => setIsEditing(true)} title="Click to edit">
                {collection.name}
              </h1>
            )}
            <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              {collection.item_count} {collection.item_count === 1 ? 'item' : 'items'}
            </span>
          </div>

          <div className="header-actions">
            {!isSelectMode && (
              <>
                {items.length > 0 && (
                  <button className="btn btn-ghost" onClick={() => setIsSelectMode(true)}>
                    Select
                  </button>
                )}
                <button className="btn btn-ghost" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
                <button className="btn btn-ghost btn-danger-text" onClick={() => setConfirmDelete(true)}>
                  Delete
                </button>
              </>
            )}
          </div>
        </header>

        {collection.description && !isEditing && (
          <div style={{
            padding: '0 var(--space-2xl)',
            marginBottom: 'var(--space-lg)',
            color: 'var(--text-muted)',
            fontSize: '0.9375rem',
          }}>
            {collection.description}
          </div>
        )}

        <div className="content-body">
          {items.length === 0 ? (
            <EmptyState
              title="This collection is empty"
              description="Browse your saved items to add some."
              action={<button className="btn btn-primary" onClick={() => navigate('/')}>Browse Items</button>}
            />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map(item => `${item.item_type}-${item.id}`)}
                strategy={rectSortingStrategy}
              >
                <div className="items-grid" style={{ padding: 'var(--space-lg) var(--space-2xl)' }}>
                  {items.map((item, index) => (
                    <SortableItem
                      key={`${item.item_type}-${item.id}`}
                      item={item}
                      isSelectMode={isSelectMode}
                      isSelected={selectedItems.has(`${item.item_type}-${item.id}`)}
                      onToggleSelect={handleToggleSelect}
                      onClick={(clickedItem) => handleItemClick(clickedItem, index)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Floating selection bar for remove mode */}
        <AnimatePresence>
          {isSelectMode && (
            <motion.div
              className="selection-bar"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <span className="selection-bar-count">{selectedItems.size} selected</span>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleRemoveSelected}
                disabled={selectedItems.size === 0 || isRemoving}
              >
                {isRemoving ? 'Removing...' : 'Remove from Collection'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setIsSelectMode(false); setSelectedItems(new Set()); }}>
                Cancel
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete confirmation */}
        <AnimatePresence>
          {confirmDelete && (
            <Modal
              onClose={() => setConfirmDelete(false)}
              labelledBy={confirmDeleteTitleId}
              className="confirm-dialog"
              panelMotionProps={CONFIRM_PANEL_MOTION}
            >
                <h3 id={confirmDeleteTitleId}>Delete "{collection.name}"?</h3>
                <p>This won't delete any saved items. They'll still appear in your library.</p>
                <div className="confirm-dialog-actions">
                  <button className="btn btn-ghost" onClick={() => setConfirmDelete(false)} disabled={isDeleting}>Cancel</button>
                  <button className="btn btn-danger" onClick={handleDeleteCollection} disabled={isDeleting}>{isDeleting ? 'Deleting...' : 'Delete'}</button>
                </div>
            </Modal>
          )}
        </AnimatePresence>
      </main>

      {/* Item Detail Modal */}
      <AnimatePresence>
        {selectedItem && (
          <ItemDetailModal
            item={selectedItem}
            onClose={handleModalClose}
            onDelete={handleDeleteItem}
            isLoadingDetails={loadingItemId !== null}
            onPrev={handleModalPrev}
            onNext={handleModalNext}
            hasPrev={hasPrevItem}
            hasNext={hasNextItem}
            itemIndex={itemTypeItems.findIndex(i => i.id === (selectedItem.id || selectedItem.item_id))}
            totalItems={itemTypeItems.length}
          />
        )}
      </AnimatePresence>

      <Toast toast={toast} />
    </div>
  );
}

// Icons
function MenuIcon() {
  return <List size={20} />;
}

function BackIcon() {
  return <CaretLeft size={20} />;
}

function DragIcon() {
  return <DotsSixVertical size={16} opacity="0.4" />;
}

function CheckIcon() {
  return <Check size={14} />;
}

function EntityIcon() {
  return <CirclesThree size={24} opacity="0.3" />;
}
